import _ from 'lodash';
import {types} from 'privates/utils';
import platformUtils from 'platformUtils';
import ui from './ui';
import * as members from 'privates/privateMembers';
import {validate} from 'privates/validations/validation';
import mediaUtils from 'privates/utils/mediaUtils';
import monitoring from 'privates/monitoring';

function getBackgroundPropertyDataPath (property) {
    const deviceType = platformUtils.getRGI().getDeviceType();
    return `pageBackgrounds.${deviceType}.ref.${property}`;
}

function getCurrentMediaRefData () {
    const RMI = members._RMI.get(this);
    const contextId = members._contextId.get(this);
    const contextData = RMI.getData(contextId);
    const mediaRefPath = getBackgroundPropertyDataPath('mediaRef');
    return _.get(contextData, mediaRefPath) || {};
}
/**
 * @typedef $w.Document~BackgroundOptions
 * @summary An object used by the `background` property that contains the background options.
 * @description
 *  Setting the `src` property of the `BackgroundOptions` object changes the
 *  displayed image or video to the image or video found at the new `src` value.
 *
 *  Getting the `src` property of the `BackgroundOptions` object returns the
 *  location of the current image or video file.
 *
 *  Images can either be from the <a href="https://support.wix.com/en/article/about-the-media-manager" target="_blank">Media Manager</a>
 *  or an external image from any web location. Videos must come from the
 *  <a href="https://support.wix.com/en/article/about-the-media-manager" target="_blank">Media Manager</a>.
 *
 *  The URL formats supported are:
 *  + Images from the Media Manager:
 *    `wix:image://v1/<uri>/<filename>/#originWidth=<width>&originHeight=<height>[&watermark=<watermark_manifest_string>]`
 *  + Videos from the Media Manager:
 *    `wix:video://v1/<video_uri>/<filename>/#posterUri=<poster_uri>&posterWidth=<width>&posterHeight=<height>`
 *  + Images from the web:
 *    `http(s)://<image url>`
 * @property {external:String} src The file location of the background image.
 * @snippet [Background-background_get.es6=Get the background information]
 * @snippet [Background-background_set_wix.es6=Set the background image to be an image from the Media Manager]
 * @snippet [Background-background_set_web.es6=Set the background image to be an image from the web]
 * @see [background](#background)
 */

/**
 * @class Document
 * @summary A complete web page, which consists of a Header, Page, and Footer.
 * @classdesc
 *  Select the current document using: `$w("Document")`.
 * @memberof $w
 */
class Document {

    constructor(RMI, compId, contextId, wixSelector, controllerId) {
        members._RMI.set(this, RMI);
        members._compId.set(this, compId);
        members._contextId.set(this, contextId);
        members._wixSelector.set(this, wixSelector);
        members._controllerId.set(this, controllerId);
    }


    /**
     * @member background
     * @label property
     * @syntax
     * get background(): BackgroundOptions
     * @summary Gets an object containing information about the document's background, such as its image or video source.
     * @type {$w.Document~BackgroundOptions}
     * @snippet [Background-background_get.es6=Get the background information]
     * @snippet [Background-background_set_wix.es6=Set the background image to be an image from the Media Manager]
     * @snippet [Background-background_set_web.es6=Set the background image to be an image from the web]
     * @memberof $w.Document
     * @readonly
     */
    get background() {
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        return new WrappedBackgroundOptions(RMI, contextId);
    }

    /**
     * @member children
     * @label property
     * @syntax
     * get children(): Node[]
     * @summary Gets the document's Header, Page, and Footer.
     * @snippet [Document-children_get.es6=Get the document's children]
     * @type {$w.Node[]}
     * @memberof $w.Document
     * @see {@link $w.Header|Header}
     * @see {@link $w.Page|Page}
     * @see {@link $w.Footer|Footer}
     * @readonly
     */
    get children() {
        const children = [];
        const childrenTypes = ['Page', 'Header', 'Footer'];
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const wixSelector = members._wixSelector.get(this);
        const controllerId = members._controllerId.get(this);

        function getInstance(compType, viewerType) {
            const Clazz = ui[compType] || Element;
            const compId = RMI.getCompIdsFromType(viewerType)[0];
            return new Clazz(RMI, compId, contextId, wixSelector, controllerId, undefined, {compType, viewerType});
        }

        childrenTypes.forEach((type) => {
            const viewerType = types.getPotentialViewerTypes(type)[0];
            children.push(getInstance(type, viewerType));
        });

        return children;
    }

    /**
     * @member type
     * @label property
     * @syntax
     * get type(): string
     * @summary Gets the document's type.
     * @description
     *  Gets the string `"$w.Document"`.
     * @snippet [Document-type_get.es6=Get the document's type]
     * @type {external:String}
     * @memberof $w.Document
     * @readonly
     */
    get type() {
        return '$w.Document';
    }
}

export default monitoring.monitorComponent(Document);


class BackgroundOptions {
    constructor(RMI, contextId) {
        members._RMI.set(this, RMI);
        members._contextId.set(this, contextId);
    }

    get src() {
        const mediaRef = getCurrentMediaRefData.call(this);
        return mediaUtils.buildFullUrlFromMediaRef(mediaRef);
    }

    set src (value) {
        if (!validate({
            compType: 'Document',
            functionName: 'backgroundImageUrl',
            dataToValidate: {url: value},
            compName: this.id
        })) {
            return;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const contextData = RMI.getData(contextId);
        const mediaRefPath = getBackgroundPropertyDataPath('mediaRef');
        const currentMediaRef = _.get(contextData, mediaRefPath) || {};

        if(_.isNil(value)) {
            _.set(contextData, mediaRefPath, null);
            RMI.setData(contextId, _.assign(contextData, {
                pageBackgrounds: contextData.pageBackgrounds
            }));
            return;
        }

        const mediaData = mediaUtils.getMediaDataFromSrc(value);
        if (!mediaData) {
            return;
        }
        if (currentMediaRef && currentMediaRef.opacity) {
            _.assign(mediaData, {opacity: currentMediaRef.opacity});
        }
        mediaUtils.getFullMediaData(mediaData, (fullMediaRefData) => {
            if (!fullMediaRefData){return;}
            const updatedMediaRefData = getCurrentMediaRefData.call(this);
            /*setDesignAsync with all media data*/
            _.set(contextData, mediaRefPath, mediaData);
            _.assign(updatedMediaRefData, fullMediaRefData);
            _.set(contextData, mediaRefPath, updatedMediaRefData);
            RMI.setData(contextId, _.assign(contextData, {
                pageBackgrounds: contextData.pageBackgrounds
            }));
        });

       _.set(contextData, mediaRefPath, mediaData);
        RMI.setData(contextId, _.assign(contextData, {
            pageBackgrounds: contextData.pageBackgrounds
        }));
    }
}

const WrappedBackgroundOptions = monitoring.monitorClass(BackgroundOptions, monitoring.getRavenContextForComponent);



// WEBPACK FOOTER //
// ./js/modules/document.es6